// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39Rs';

import {
  resetUserAsAdminSite,
  selectIsUserSubEmissionSite,
} from 'redux/slices/userSlice';
import { useDispatch, useSelector } from 'react-redux';
import { useMemo, useState } from 'react';
import { OFFLINE_USAGE_SUPPORT_ROUTES, firstPage, Path } from 'routes';
import { selectUser, UserState } from 'redux/slices/userSlice';
import {
  CompressionSiteType,
  OperatorType,
  ProcessingMode,
  RoleType,
  TenantAuthen,
} from 'util/Enums';
import { Link } from 'react-router-dom';
import { useHistory } from 'react-router-dom';
import { IconMenu } from 'components/atoms/icons/IconMenu';
import styles from './Navbar.module.scss';
import { IconClose2 } from 'components/atoms/icons/IconClose2';
import { IconClipBoardList } from 'components/atoms/icons/IconClipBoardList';
import { IconLogOut } from 'components/atoms/icons/IconLogOut';
import { useTranslation } from 'react-i18next';
import { IconOutlineFile } from 'components/atoms/icons/IconOutlineFile';
import IconPool from 'components/atoms/icons/IconPool';
import IconSetting from 'components/atoms/icons/IconSetting';
import IconHistory from 'components/atoms/icons/IconHistory';
import IconListAccept from 'components/atoms/icons/IconListAccept';
import IconBox from 'components/atoms/icons/IconBox';
import { IconBarcodeV2 } from 'components/atoms/icons/IconBarcodeV2';
import { IconScan } from 'components/atoms/icons/IconScan';
import useOutsideClick from 'util/hooks/useClickOutside';
import IconDashboard from 'components/atoms/icons/IconDashboard';
import ConfirmLogoutModal from 'components/molecules/ConfirmLogoutModal';
import IconShipping from 'components/atoms/icons/IconShipping';
import { fnLogout, mergeClasses } from 'util/commons';
import CheckPasswordAndConfirmLogoutModal from 'components/molecules/CheckPasswordAndConfirmLogoutModal';
import LanguageSelect from 'components/atoms/LanguageSelect';
import { IconScale } from 'components/atoms/icons/IconScale';
import { toast } from 'react-toastify';
import IconCategory from 'components/atoms/icons/IconCategory';
import { IconExport } from 'components/atoms/icons/IconExport';
import { IconUser } from 'components/atoms/icons/IconUser';
import useNetworkConnection from 'util/hooks/useNetworkConnection';
import { OFFLINE_USAGE_SUPPORT_PERMISSIONS } from 'util/ConstantValues';
import { version } from '../../../../package.json';
import { IconChevronUp } from 'components/atoms/icons/IconChevronUp';
import { IconChevronDown } from 'components/atoms/icons/IconChevronDown';
import { IconKey } from 'components/atoms/icons/IconKey';
import { IconICCardScanHistory } from 'components/atoms/icons/IconICCardScanHistory';
import Popover from 'components/atoms/Popover';
import { createAxios } from 'ts/createAxios';
import { handleError } from 'util/errorHandler';
import { API_ENDPOINTS } from 'util/endPoints';
import IconFileImport from 'components/atoms/icons/IconFileImport';
import { IconRouteSetting } from 'components/atoms/icons/IconRouteSetting';
import { IconAvatar } from 'components/atoms/icons/IconAvatar';

type Permission<T> = {
  accept?: T[];
  deny?: T[];
};

type NavItem = NavBarItem & {
  subItems?: NavBarItem[];
};

type NavBarItem = {
  link: string;
  label: string;
  icon?: React.ReactNode;
  childrenLinks?: string[];
  permissions?: {
    role?: Permission<RoleType>;
    operatorType?: Permission<OperatorType>;
    customCondition?: boolean;
  }[];
};

type NavbarProps = {
  placement?: 'left' | 'right';
};

export function Navbar({ placement = 'right' }: NavbarProps) {
  const user = useSelector(selectUser) as UserState;
  const isUserSubEmissionSite = useSelector(selectIsUserSubEmissionSite);
  const history = useHistory();
  const dispatch = useDispatch();
  const path = history.location.pathname;
  const isActive = (routes: string) => {
    if (path === routes || path.includes(routes)) {
      return 'active';
    } else {
      return '';
    }
  };
  const isWebview = window.navigator.userAgent.includes('wv');
  const { t } = useTranslation();
  const [isShowNavBar, setIsShowNavBar] = useState(false);
  const [isConfirmLogout, setIsConfirmLogout] = useState(false);
  const [isShowSubItems, setIsShowSubItems] = useState(false);

  const isOnline = useNetworkConnection();

  const handleSignOut = () =>
    fnLogout(history, () => setIsConfirmLogout(false));

  const handleMenuIcon = () => {
    setIsShowNavBar(true);
  };

  const onClickOutside = () => {
    setIsShowNavBar(false);
  };

  const { ref } = useOutsideClick({ onClickOutside });

  const handleDownloadPoolApp = async () => {
    try {
      const {
        data: { downloadUrl },
      } = await createAxios().get<{
        downloadUrl: string;
      }>(API_ENDPOINTS.DOWNLOAD_POOL_APP);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = '';
      link.target = '_blank';
      link.rel = 'noopener noreferrer';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) {
      handleError(e);
    }
  };

  const labelSliceToDisplay = (
    label: string,
    maxNumOfCharacterDisplay = 20
  ) => {
    if (!label) {
      return '';
    }
    return label.length > maxNumOfCharacterDisplay
      ? `${label.slice(0, maxNumOfCharacterDisplay)}...`
      : label;
  };

  const renderNavItem = (navItem: NavItem[]) =>
    navItem.map(
      ({ link, icon, label, childrenLinks, permissions, subItems }, index) => {
        const permissionGranted =
          !permissions?.length ||
          permissions.some((permission) => {
            const { role, operatorType, customCondition = true } = permission;

            if (!customCondition) {
              return false;
            }

            if (role) {
              if (
                role.accept?.length &&
                (!user.role || !role.accept.includes(user.role))
              ) {
                return false;
              }

              if (
                role.deny?.length &&
                (!user.role || role.deny.includes(user.role))
              ) {
                return false;
              }
            }

            if (operatorType) {
              if (
                operatorType.accept?.length &&
                (!user.operatorType ||
                  !operatorType.accept.includes(
                    user.operatorType as OperatorType
                  ))
              ) {
                return false;
              }

              if (
                operatorType.deny?.length &&
                (!user.operatorType ||
                  operatorType.deny.includes(user.operatorType as OperatorType))
              ) {
                return false;
              }
            }

            return true;
          });

        if (
          !permissionGranted ||
          (OFFLINE_USAGE_SUPPORT_PERMISSIONS.some(
            ({ role, operatorType }) =>
              user.role &&
              role.includes(user.role) &&
              user.operatorType &&
              operatorType.includes(user.operatorType as OperatorType)
          ) &&
            !isOnline &&
            !OFFLINE_USAGE_SUPPORT_ROUTES.includes(link))
        ) {
          return null;
        }

        return subItems && subItems.length > 0 ? (
          renderSubItems(link, label, subItems, icon, childrenLinks)
        ) : (
          <li
            key={index}
            className={mergeClasses({
              [styles.active]:
                isActive(link) || childrenLinks?.some((li) => isActive(li)),
            })}
          >
            <Link to={link}>
              {icon}
              <span>{label}</span>
            </Link>
          </li>
        );
      }
    );

  const renderSubItems = (
    link: string,
    label: string,
    subItems: NavBarItem[],
    icon?: React.ReactNode,
    childrenLinks?: string[]
  ) => {
    return (
      <li
        className={mergeClasses(styles.hasSubItems, {
          [styles.active]:
            subItems.some((item) => isActive(item.link)) ||
            childrenLinks?.some((li) => isActive(li))
              ? 'active'
              : '',
        })}
      >
        <div className={styles.parentItem}>
          <Link
            to={link}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              setIsShowSubItems(true);
            }}
          >
            {icon}
            <span>{label}</span>
          </Link>

          {isShowSubItems ? (
            <IconChevronUp onClick={() => setIsShowSubItems(false)} />
          ) : (
            <IconChevronDown onClick={() => setIsShowSubItems(true)} />
          )}
        </div>

        {isShowSubItems && (
          <ul className={styles.subItem}>
            {subItems.map((item, index) => (
              <li
                className={mergeClasses({
                  [styles.active]:
                    isActive(item.link) ||
                    item.childrenLinks?.some((li) => isActive(li)),
                })}
                key={index}
              >
                <Link to={item.link}>
                  {item.icon}
                  <span>{item.label}</span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </li>
    );
  };

  const navItems = useMemo(
    (): NavItem[] => [
      // Setting route
      {
        link: Path.routeSettings,
        icon: <IconRouteSetting />,
        label: t('nav.route_settings'),
        permissions: [
          {
            role: {
              accept: [RoleType.AdminOperator, RoleType.AdminSite],
            },
            operatorType: {
              accept: [OperatorType.Collects],
            },
          },
        ],
      },
      // Collect
      {
        link: Path.routeList,
        icon: <IconOutlineFile />,
        label: t('nav.collect'),
        permissions: [
          {
            role: {
              accept: [
                RoleType.AdminOperator,
                RoleType.UserNormal,
                RoleType.AdminSite,
              ],
            },
            operatorType: {
              accept: [OperatorType.Collects],
            },
          },
        ],
      },
      // Compression
      {
        link: Path.acceptances,
        icon: <IconListAccept />,
        label: t('nav.acceptance'),
        permissions: [
          {
            operatorType: {
              accept: [OperatorType.Compressions],
            },
            role: {
              accept: [RoleType.AdminSite],
            },
          },
        ],
      },
      {
        link: Path.storageLocationRegistration,
        icon: <IconRouteSetting />,
        label: t('nav.storage_location_registration'),
        permissions: [
          {
            operatorType: {
              accept: [OperatorType.Compressions],
            },
            role: {
              accept: [RoleType.AdminSite],
            },
            customCondition: user.siteType === CompressionSiteType.Storage,
          },
        ],
      },

      // Recycle
      {
        link: Path.recycle,
        icon: <IconListAccept />,
        label: t('nav.recycle'),
        permissions: [
          {
            operatorType: {
              accept: [OperatorType.Recycle],
            },
          },
        ],
      },
      {
        link: Path.recycleProductRegistration,
        icon: <IconOutlineFile />,
        label: t('nav.recycle_product_registration'),
        permissions: [
          {
            operatorType: {
              accept: [OperatorType.Recycle],
            },
          },
        ],
      },
      // Separates
      {
        link: Path.recycle,
        label: t('nav.recycle_separates'),
        permissions: [
          {
            operatorType: {
              accept: [OperatorType.Separates],
            },
          },
        ],
      },
      // Emission
      {
        link: Path.dashboard,
        icon: <IconDashboard />,
        label: t('dashboard.title'),
        permissions: [
          {
            operatorType: {
              accept: [OperatorType.Brand],
            },
            role: {
              accept: [RoleType.BrandOwner],
            },
          },
          {
            operatorType: {
              accept: [OperatorType.Emissions],
            },
            role: {
              accept: [RoleType.AdminOperator, RoleType.AdminSite],
            },
          },
        ],
      },
      {
        link: Path.poolDashboard,
        icon: <IconDashboard />,
        label: t('dashboard.title'),
        permissions: [
          {
            role: {
              accept: [RoleType.AdminPool],
            },
          },
        ],
      },
      {
        link: Path.siteManagement,
        icon: <IconCategory />,
        label: t('nav.site_management'),
        permissions: [
          {
            operatorType: {
              accept: [OperatorType.Emissions],
            },
            role: {
              accept: [RoleType.AdminOperator],
            },
          },
        ],
      },
      {
        link: Path.tenantDashboard,
        icon: <IconDashboard />,
        label: t('dashboard.title'),
        permissions: [
          {
            operatorType: {
              accept: [OperatorType.Emissions],
            },
            role: {
              accept: [RoleType.AdminTenant],
            },
          },
        ],
      },
      {
        link: Path.tenantManagement,
        icon: <IconUser width={24} height={24} />,
        label: t('nav.tenant_management'),
        permissions: [
          {
            operatorType: {
              accept: [OperatorType.Emissions],
            },
            role: {
              accept: [RoleType.AdminSite],
            },
          },
        ],
      },
      {
        link: Path.poolExportDataInvoice,
        icon: <IconExport />,
        label: t('nav.export_data_invoice'),
        permissions: [
          {
            role: {
              accept: [RoleType.AdminPool],
            },
          },
        ],
      },
      {
        link: Path.exportDataInvoice,
        icon: <IconExport />,
        label: t('nav.export_data_invoice'),
        permissions: [
          {
            operatorType: {
              accept: [OperatorType.Emissions],
            },
            role: {
              accept: [RoleType.AdminSite],
            },
          },
        ],
      },
      {
        link: '',
        icon: <IconFileImport />,
        label: t('nav.batch_registration'),
        permissions: [
          {
            operatorType: {
              accept: [OperatorType.Emissions],
            },
            role: {
              accept: [RoleType.AdminSite],
            },
          },
        ],
        subItems: [
          {
            link: Path.importCSV,
            label: t('nav.importCSV'),
          },
          {
            link: Path.batchRegistration,
            label: t('nav.batch_registration'),
          },
        ],
      },
      {
        link:
          user.isShowTenants || isUserSubEmissionSite
            ? Path.selectTenant
            : Path.emissionNew,
        icon: <IconPool />,
        label: isUserSubEmissionSite
          ? t('nav.resource_registration')
          : t('nav.emission'),
        childrenLinks: [
          Path.emissionNew,
          Path.selectTenant,
          Path.emissionNewDetail,
        ],
        permissions: [
          {
            operatorType: {
              accept: [OperatorType.Emissions],
            },
            role: {
              deny: [RoleType.AdminOperator, RoleType.AdminTenant],
            },
          },
          {
            operatorType: {
              accept: [OperatorType.Recycle],
            },
            role: {
              accept: [RoleType.AdminOperator],
            },
            customCondition: user.enableSubSite,
          },
          {
            operatorType: {
              accept: [OperatorType.Compressions],
            },
            role: {
              accept: [RoleType.AdminSite],
            },
            customCondition:
              user.enableSubSite &&
              user.siteType !== CompressionSiteType.Storage,
          },
        ],
      },
      {
        link: Path.batchRegistration,
        icon: <IconFileImport />,
        label: t('nav.batch_registration'),
        permissions: [
          {
            operatorType: {
              accept: [OperatorType.Emissions],
            },
            role: {
              accept: [RoleType.UserNormal],
            },
          },
        ],
      },
      {
        link: Path.unCollects,
        icon: <IconClipBoardList />,
        label: t('nav.un_collect'),
        permissions: [
          {
            operatorType: {
              accept: [OperatorType.Emissions],
            },
            role: {
              deny: [RoleType.AdminOperator, RoleType.AdminTenant],
            },
          },
        ],
      },
      {
        link: Path.tareWeightSubtraction,
        icon: <IconSetting />,
        label: t('nav.tare_management'),
        permissions: [
          {
            operatorType: {
              accept: [OperatorType.Emissions],
            },
            role: {
              deny: [RoleType.AdminOperator, RoleType.AdminTenant],
            },
          },
        ],
      },
      {
        link: Path.barcodes,
        icon: <IconBarcodeV2 />,
        label: t('barcodes.heading'),
        permissions: [
          {
            operatorType: {
              accept: [OperatorType.Emissions],
            },
            role: {
              accept: [RoleType.AdminSite],
            },
            customCondition: user.originalTenantAuthen === TenantAuthen.Barcode,
          },
        ],
      },

      {
        link: Path.emissionQRScanning,
        icon: <IconRouteSetting />,
        label: t('nav.storage_location_registration'),
        permissions: [
          {
            operatorType: {
              accept: [OperatorType.Emissions],
            },
            role: {
              accept: [RoleType.AdminSite, RoleType.UserNormal],
            },
            customCondition: user.temporaryStorageEnabled,
          },
        ],
      },

      // Histories menu is a common item
      {
        link: Path.histories,
        icon: <IconHistory />,
        label: t('nav.histories'),
        permissions: [
          {
            operatorType: {
              deny: [
                OperatorType.Admin,
                OperatorType.Brand,
                OperatorType.Emissions,
                OperatorType.Compressions,
                OperatorType.Recycle,
              ],
            },
            role: {
              deny: [RoleType.AdminPool],
            },
          },
        ],
      },
      // EmissionHistories item
      {
        link: Path.emissionHistories,
        icon: <IconHistory />,
        label: t('nav.histories'),
        permissions: [
          {
            operatorType: {
              accept: [OperatorType.Emissions],
            },
          },
        ],
      },

      // Histories menu for compression and recycle site
      {
        link: '',
        icon: <IconHistory />,
        label: t('nav.histories'),
        permissions: [
          {
            operatorType: {
              accept: [OperatorType.Recycle],
            },
            role: {
              accept: [RoleType.AdminOperator],
            },
          },
        ],
        subItems: [
          {
            link: Path.histories,
            label: t('nav.processing_registration_histories'),
          },
          {
            link: Path.resourceRegistrationHistories,
            label: t('nav.resource_registration_histories'),
          },
        ],
      },
      // Emission: 外部計量器連携
      {
        link: Path.externalScaleConnectionsHistory,
        icon: <IconScale />,
        label: t('nav.external_scale_connections'),
        permissions: [
          {
            operatorType: {
              accept: [OperatorType.Emissions],
            },
            role: {
              accept: [RoleType.AdminSite, RoleType.UserNormal],
            },
          },
        ],
      },
      {
        link: Path.scanICCard,
        icon: <IconScan />,
        label: t('nav.scan_ic_card'),
        permissions: [
          {
            operatorType: {
              accept: [OperatorType.Emissions],
            },
            role: {
              accept: [RoleType.AdminSite, RoleType.UserNormal],
            },
            customCondition: user.originalTenantAuthen === TenantAuthen.ICCard,
          },
        ],
      },
      {
        link: Path.ICCardScanHistory,
        icon: <IconICCardScanHistory width={24} height={24} />,
        label: t('nav.ic_card_scan_history'),
        permissions: [
          {
            operatorType: {
              accept: [OperatorType.Emissions],
            },
            role: {
              accept: [RoleType.AdminSite, RoleType.UserNormal],
            },
            customCondition: user.originalTenantAuthen === TenantAuthen.ICCard,
          },
        ],
      },
      {
        link: Path.processingRegistration,
        icon: <IconBox />,
        label: t('nav.processing_registration'),
        permissions: [
          {
            operatorType: {
              accept: [OperatorType.Compressions],
            },
            role: {
              accept: [RoleType.AdminSite],
            },
            customCondition: user.siteType !== CompressionSiteType.Storage,
          },
        ],
      },
      {
        link: Path.shipment,
        icon: <IconShipping />,
        label: t('nav.shipment_registration'),
        permissions: [
          {
            operatorType: {
              accept: [OperatorType.Compressions],
            },
            role: {
              accept: [RoleType.AdminSite],
            },
          },
        ],
      },
      {
        link: Path.processingMaster,
        icon: <IconSetting />,
        label: t('nav.processing_master'),
        permissions: [
          {
            operatorType: {
              accept: [OperatorType.Recycle],
            },
            role: {
              accept: [RoleType.AdminOperator],
            },
            customCondition: user.processingMode === ProcessingMode.Auto,
          },
          {
            operatorType: {
              accept: [OperatorType.Compressions],
            },
            role: {
              accept: [RoleType.AdminSite],
            },
            customCondition:
              user.processingMode === ProcessingMode.Auto &&
              user.siteType !== CompressionSiteType.Storage,
          },
        ],
      },
      {
        link: Path.siteUserManagement,
        icon: <IconKey width={24} height={24} />,
        label: t('nav.user_security'),
        permissions: [
          {
            operatorType: {
              accept: [OperatorType.Emissions],
            },
            role: {
              accept: [RoleType.AdminSite, RoleType.AdminOperator],
            },
          },
          {
            operatorType: {
              accept: [OperatorType.Brand],
            },
            role: {
              accept: [RoleType.BrandOwner],
            },
          },
          {
            operatorType: {
              accept: [OperatorType.Compressions, OperatorType.Recycle],
            },
            role: {
              accept: [RoleType.AdminOperator, RoleType.AdminSite],
            },
          },
          {
            operatorType: {
              accept: [OperatorType.Collects],
            },
            role: {
              accept: [RoleType.AdminOperator, RoleType.AdminSite],
            },
          },
        ],
      },
      {
        link: Path.settlementAutoGeneration,
        icon: <IconExport />,
        label: t('nav.settlement_auto_generation'),
        permissions: [
          {
            operatorType: {
              accept: [OperatorType.Collects],
            },
            role: {
              accept: [RoleType.AdminOperator, RoleType.AdminSite],
            },
          },
        ],
      },
      {
        link: Path.poolUserSecurity,
        icon: <IconKey width={24} height={24} />,
        label: t('nav.user_security'),
        permissions: [
          {
            role: {
              accept: [RoleType.AdminPool],
            },
          },
        ],
      },
      {
        link: Path.compressionHistories,
        icon: <IconHistory />,
        label: t('nav.histories'),
        permissions: [
          {
            operatorType: {
              accept: [OperatorType.Compressions],
            },
            role: {
              accept: [RoleType.AdminSite],
            },
          },
        ],
      },
      {
        link: Path.myPage,
        icon: <IconAvatar />,
        label: t('nav.my_page'),
        permissions: [
          {
            operatorType: {
              accept: [OperatorType.Emissions],
            },
            role: {
              accept: [RoleType.AdminSite, RoleType.AdminOperator],
            },
          },
          {
            operatorType: {
              accept: [OperatorType.Collects],
            },
            role: {
              accept: [
                RoleType.AdminOperator,
                RoleType.UserNormal,
                RoleType.AdminSite,
              ],
            },
          },
          {
            operatorType: {
              accept: [OperatorType.Compressions, OperatorType.Recycle],
            },
            role: {
              accept: [RoleType.AdminOperator],
            },
          },
          {
            role: {
              accept: [RoleType.AdminPool],
            },
          },
        ],
      },
    ],
    [isWebview, user, t]
  );

  return (
    <div className={styles.navBar}>
      <div className={styles.hamburgerMenu}>
        {isShowNavBar === false && (
          <div
            className={mergeClasses(styles.iconMenu, {
              [styles.iconMenuFlipped]: placement === 'left',
            })}
            onClick={handleMenuIcon}
          >
            <IconMenu />
          </div>
        )}
      </div>

      {isShowNavBar === true && (
        <div className={styles.menuContentAll}>
          <div
            className={mergeClasses(styles.menuContents, {
              [styles.menuContentsLeft]: placement === 'left',
            })}
            ref={ref}
          >
            <div className={styles.menuLinks}>
              <div
                className={styles.iconClose}
                onClick={() => setIsShowNavBar(false)}
              >
                <IconClose2 />
              </div>

              <div className={styles.menuTitle}>
                <Popover
                  content={t('nav.download_pool_app')}
                  positions={['top']}
                  containerStyle={{
                    zIndex: '100000',
                  }}
                >
                  <img
                    src="/assets/images/logo.svg"
                    alt="logo"
                    onClick={handleDownloadPoolApp}
                  />
                </Popover>
              </div>
              <div className={styles.menuSubTitleContainer}>
                <h4 className={styles.menuSubtitle}>{user.name}</h4>
              </div>

              <div className={styles.siteNameAndLanguages}>
                <div className={styles.siteName} title={user.siteName}>
                  {labelSliceToDisplay(
                    user.role === RoleType.AdminOperator ||
                      user.role === RoleType.BrandOwner
                      ? user.operatorName
                      : user.role === RoleType.AdminSite ||
                        user.role === RoleType.UserNormal
                      ? user.siteName
                      : '',
                    20
                  )}
                </div>

                <div className={styles.languages}>
                  <LanguageSelect />
                </div>
              </div>
              <ul>
                {renderNavItem(navItems)}

                {/** ログアウト */}
                <li
                  className="logout"
                  key="logout"
                  onClick={() => {
                    if (user.temporaryAdminSite) {
                      dispatch(resetUserAsAdminSite());
                      const redirect = firstPage(user);
                      history.push(redirect);
                      toast.info(t('messages.M_temporary_admin_site_logout'));
                      return;
                    }
                    setIsConfirmLogout(true);
                  }}
                >
                  <div
                    className={mergeClasses(styles.logout, {
                      [styles.logoutFlipped]: placement === 'left',
                    })}
                  >
                    <IconLogOut />
                    <span>{t('nav.logout')}</span>
                  </div>
                </li>
                <li key="version" className={styles.version}>
                  Ver {version}
                </li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {isConfirmLogout &&
        (user.operatorType === OperatorType.Emissions &&
        user.role === RoleType.UserNormal ? (
          <CheckPasswordAndConfirmLogoutModal
            onClose={() => setIsConfirmLogout(false)}
            onSubmit={handleSignOut}
          />
        ) : (
          <ConfirmLogoutModal
            onClose={() => setIsConfirmLogout(false)}
            onSubmit={handleSignOut}
          />
        ))}
    </div>
  );
}
